import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form } from 'antd';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { Input } from '@/shared/components';
import { DateTimeIso } from '@/shared/types';
import { DatePicker } from '@/shared/uikit/DatePicker';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { ConfirmApprovalModalProps } from '@/widgets/modals/types';

import { ModalContainerV2 } from '../containers/ModalContainerV2';

export type ConfirmApprovalFormData = {
  placeOfExecution: string;
  dateOfExecution: DateTimeIso;
};

export const ConfirmApprovalModal: FC<ConfirmApprovalModalProps> = ({ onConfirm }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'confirmApproval' });
  const modals = useModalsStore();
  const { control, formState, handleSubmit: formHandleSubmit } = useForm<ConfirmApprovalFormData>();

  const handleSubmit = (data: ConfirmApprovalFormData) => {
    modals.closeModal();
    onConfirm(data);
  };

  return (
    <ModalContainerV2 title={t('title')} pinBottom>
      <StyledForm onFinish={formHandleSubmit(handleSubmit)}>
        <FormWrapper>
          <p>{t('description1')}</p>
          <Controller
            name="placeOfExecution"
            control={control}
            rules={{ required: t('fields.placeOfExecution.error') }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw required label={t(`fields.${field.name}.label`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
          <Controller
            name="dateOfExecution"
            control={control}
            rules={{ required: t('fields.dateOfExecution.error') }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw required label={t(`fields.${field.name}.label`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                    style={{ maxWidth: '280px' }}
                  >
                    <DatePicker placeholder={t(`fields.${field.name}.placeholder`)} {...field} />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
        </FormWrapper>
        <p>{t('description2')}</p>
        <ButtonWrapper>
          <ButtonIw type="default" onClick={() => modals.closeModal()}>
            {t('cancelButton')}
          </ButtonIw>
          <ButtonIw
            htmlType="submit"
            loading={formState.isSubmitting}
            disabled={formState.isSubmitting}
          >
            {t('confirmButton')}
          </ButtonIw>
        </ButtonWrapper>
      </StyledForm>
    </ModalContainerV2>
  );
};

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  gap: 16px;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 24px;

  &&& {
    padding-top: 24px;
  }
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
